import { useState, useRef, useCallback } from 'react';
import { QRInput } from './components/QRInput';
import { QROptions } from './components/QROptions';
import { QRPreview, type QRPreviewHandle } from './components/QRPreview';
import { DownloadButton } from './components/DownloadButton';
import type { QRCodeConfig } from './types';
import { DEFAULT_CONFIG } from './types';
import './App.css';

export default function App() {
  const [config, setConfig] = useState<QRCodeConfig>(DEFAULT_CONFIG);
  const previewRef = useRef<QRPreviewHandle>(null);
  const logoInputRef = useRef<HTMLInputElement>(null);

  const handleTextChange = useCallback((text: string) => {
    setConfig((prev) => ({ ...prev, text }));
  }, []);

  const handleConfigChange = useCallback((updates: Partial<QRCodeConfig>) => {
    setConfig((prev) => ({ ...prev, ...updates }));
  }, []);

  const handleDownload = useCallback(() => {
    previewRef.current?.download();
  }, []);

  const handleQuickAction = useCallback((type: string) => {
    switch (type) {
      case 'url':
        handleTextChange('https://');
        break;
      case 'text':
        handleTextChange('');
        break;
      case 'wifi': {
        const wifi = `WIFI:T:WPA;S:MyWiFi;P:password;;`;
        handleTextChange(wifi);
        break;
      }
      case 'email':
        handleTextChange('mailto:example@example.com');
        break;
    }
  }, [handleTextChange]);

  return (
    <div className="app">
      <header className="app-header">
        <div className="header-row">
          <div className="header-brand">
            <span className="header-logo">◼</span>
            <h1 className="header-title">QR码生成器</h1>
          </div>
          <div className="header-actions">
            <span className="quick-label">快捷:</span>
            <button className="btn btn-xs" onClick={() => handleQuickAction('url')} type="button">🔗 链接</button>
            <button className="btn btn-xs" onClick={() => handleQuickAction('text')} type="button">📝 文本</button>
            <button className="btn btn-xs" onClick={() => handleQuickAction('wifi')} type="button">📶 WiFi</button>
            <button className="btn btn-xs" onClick={() => handleQuickAction('email')} type="button">✉️ 邮件</button>
          </div>
        </div>
      </header>

      <main className="app-main">
        <div className="main-layout">
          {/* Left: Preview + Download */}
          <section className="preview-col">
            <QRPreview ref={previewRef} config={config} />
            <DownloadButton
              onDownload={handleDownload}
              disabled={!config.text.trim()}
            />
          </section>

          {/* Right: Controls */}
          <section className="controls-col">
            <QRInput value={config.text} onChange={handleTextChange} />
            <QROptions
              config={config}
              onChange={handleConfigChange}
              logoInputRef={logoInputRef}
            />
          </section>
        </div>
      </main>
    </div>
  );
}
